// NPM
import { isEmpty, omitBy, isNil } from "lodash";
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

// HELPERS
import { encode } from "Helpers/index";

// STYLE
import "./delivery-lists.scss";

// COMPONENTS
import { Col, Row } from "antd";
import { ActionMenu, Button, DataTable, SelectInput } from "@life-line-lab/components-react";
import { MoreOutlined } from "@ant-design/icons";
import DataTableFilter from "Components/shared/data-table-filter";

interface RackListProps {
  offset: number;
  list?: any;
  onFilter?: (filterInput) => void;
  onUpdateStatus: (value: string, data: any) => void;
}

const RackList: React.FC<RackListProps> = ({ offset, list, onFilter, onUpdateStatus }) => {
  const history = useHistory();
  const { t } = useTranslation();

  const columns = [
    {
      title: t("label.no.").toLocaleUpperCase(),
      dataIndex: "index",
      render: (_: any, record: any, index: number) => <span>{index + 1 + offset}</span>,
    },
    {
      title: t("label.rackID").toLocaleUpperCase(),
      dataIndex: "code",
      render: (_: any, record: any) => <a className="clickable-id">{record.rack?.code}</a>,
      onCell: (record: any) => {
        return {
          onClick: (e) => handleViewAsset(record.rack?._id),
        };
      },
    },
    {
      title: t("label.rackName").toLocaleUpperCase(),
      dataIndex: ["rack", "name"],
    },
    {
      title: t("label.rackLocation").toLocaleUpperCase(),
      dataIndex: ["servicePoint", "locationName"],
    },
    {
      title: t("label.noOfSlots").toLocaleUpperCase(),
      dataIndex: "noOfSlots",
      render: (_: any, record: any) => (
        <span>{(record.rack?.noOfRows * record.rack?.noOfColumns).toLocaleString()}</span>
      ),
    },
    {
      title: t("label.availability").toLocaleUpperCase(),
      dataIndex: ["rack", "status"],
      render: (text: any, record: any) => (
        <span>
          {text === "OPEN" ? t("label.available") : record?.rack?.status ? t("label.full") : ""}
        </span>
      ),
    },
    {
      title: t("label.status").toLocaleUpperCase(),
      dataIndex: "active",
      render: (_: any, record: any) => (
        <SelectInput
          className={`select select--${`${record.rack?.active}`.toLowerCase()}`}
          value={`${record.rack?.active}`}
          options={[
            {
              label: "ACTIVE",
              value: "true",
            },
            {
              label: "INACTIVE",
              value: "false",
            },
          ]}
          onChange={(value) => onUpdateStatus(value as any, record.rack)}
        />
      ),
    },
    {
      title: "",
      dataIndex: "action",
      key: "action",
      width: 40,
      render: (_: any, record: any) => (
        <ActionMenu
          label={<MoreOutlined />}
          actions={[
            {
              label: t("action.edit"),
              onClick: () => {
                handleUpdateAsset(record.rack?._id);
              },
            },
          ]}
        />
      ),
    },
  ];

  const initialFilters: any = {
    orFields: "",
  };

  const [filters, setFilters] = useState<{ [key: string]: string | boolean }>(initialFilters);

  useEffect(() => {
    if (onFilter && !isEmpty(filters)) {
      onFilter(omitBy(filters, (input) => isNil(input) || isEmpty(input)));
    }
  }, [filters]);

  const handleSearch = async (text: string) => {
    setFilters((filters) => ({ ...filters, orFields: text }));
  };

  const handleViewAsset = (id: string) => {
    const encodedID = encode(id);
    history.push(`/delivery-management/settings/assets?id=${encodedID}`);
  };

  const handleCreateAsset = () => {
    history.push(`/delivery-management/settings/assets/form/0`);
  };

  const handleUpdateAsset = (id: string) => {
    const encodedID = encode(id);
    history.push(`/delivery-management/settings/assets/form/0?id=${encodedID}`);
  };

  const handleBoxTrolleyPage = () => {
    history.push(`/fullscreen/tracking?redirect=dm_setup&type=0&return=racks`);
  };

  return (
    <div className="delivery-list rack">
      <DataTable
        rowKey={(record: any) => record.rack?._id}
        columns={columns as any}
        dataSource={list || []}
        scroll={{ x: true }}
        title={() => (
          <Row className="title-row" justify="space-between">
            <Col className="mb-0">
              <DataTableFilter
                searchPlaceholder={t("label.searchBy") + " " + t("label.rack")}
                onSearch={(text: string) => handleSearch(text)}
              />
            </Col>
            <Col className="mb-0">
              <span style={{ marginRight: 16 }}>
                <a onClick={handleBoxTrolleyPage}>{t("label.fullscreen")} </a>
              </span>
              <Button
                className="full-height"
                label={t("action.addField", { field: t("label.rack") })}
                type="primary"
                onClick={handleCreateAsset}
              />
            </Col>
          </Row>
        )}
      />
    </div>
  );
};

export default RackList;
